import { Injectable } from '@angular/core';
import { v4 as uuid } from 'uuid';

import { DprProgressGridData } from '@dpr-details/models/grid';
import { DprProgressDetail } from '@shared/models/dpr';
import { ProgressItem } from '@shared/models/project-setup';
import { Sync } from '@shared/models/grid';
import { SyncService } from '@shared/services/grid';

@Injectable()
export class DprProgressGridService {
  constructor(private syncService: SyncService) {}

  buildGridData(
    dprProgressItems: DprProgressDetail[],
    progressItems: ProgressItem[]
  ): DprProgressGridData[] {
    const activeProgressItems = progressItems.filter((x) => x.isActive);

    return dprProgressItems.reduce((result, dprProgressItem) => {
      const progressItem = activeProgressItems.find(
        (x) => x.id === dprProgressItem.progressId
      );

      return progressItem
        ? [
            ...result,
            {
              id: dprProgressItem.id ?? uuid(),
              priority: progressItem.priority,
              progressId: progressItem.id,
              name: progressItem.name,
              isMandatory: progressItem.isMandatory,
              clearedToday: dprProgressItem?.clearedToday,
              clearedTotal: dprProgressItem.progressStockDetails.clearedTotal,
              clearedTotalInitialValue:
                (dprProgressItem.progressStockDetails.clearedTotal || 0) -
                (dprProgressItem?.clearedToday || 0),
              percentComplete:
                dprProgressItem.progressStockDetails.percentComplete,
              targetTotal: progressItem.initialValue,
              targetLeft: dprProgressItem.progressStockDetails.totalLeft,
              comment: dprProgressItem.comment,
            },
          ]
        : result;
    }, []);
  }

  mapToGridSync(
    changes: Sync<DprProgressGridData>,
    dprProgressId: number | string
  ): Sync<DprProgressDetail> {
    const result = this.syncService.getDefaultSyncModel<DprProgressDetail>();

    const mappingFunction = (data: DprProgressGridData) => ({
      id: data.id,
      dprProgressId,
      progressId: data.progressId,
      clearedToday: data.clearedToday,
      comment: data.comment,
      progressStockDetails: {
        clearedTotal: data.clearedTotal,
        totalLeft: data.targetLeft,
        percentComplete: data.percentComplete,
      },
    });

    result.addedList.push(...(changes?.addedList?.map(mappingFunction) ?? [])); // needed for offline
    result.addedList.push(
      ...(changes?.updatedList
        ?.filter((x) => isNaN(+x.id))
        .map(mappingFunction) ?? [])
    );

    result.updatedList = changes?.updatedList
      ?.filter((x) => !isNaN(+x.id))
      .map(mappingFunction);

    return result;
  }

  shouldAutoFill(data: DprProgressGridData[]): boolean {
    if (!data.length) {
      return false;
    }

    const someItemsFilled = data.some((x) => x.clearedToday != null);
    const someItemsEmpty = data.some((x) => x.clearedToday == null);

    return someItemsEmpty && someItemsFilled;
  }
}
